import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, googleProvider } from "../services/firebaseService";
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  User,
  UserCredential,
} from "firebase/auth";
import i18n from "../i18n"; // Importer i18n pour changer la langue
import LoadingOverlay from "../components/LoadingOverlay";

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  isAdmin: boolean;
  userPlan: string;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  signupWithEmail: (email: string, password: string, companyName: string, howDidYouHear: string) => Promise<UserCredential>;
  loginWithCustomToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  checkLoginMethods: (email: string) => Promise<string[]>;
  getToken: () => Promise<string | null>;
  changeLanguage: (language: string) => Promise<void>;
  currentLanguage: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [userPlan, setUserPlan] = useState<string>("free");
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState<string>("fr");

  useEffect(() => {
    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mounted) return;
      
      setCurrentUser(user);
      
      if (user) {
        try {
            // Récupérer le token et le profil pour la langue
            const token = await user.getIdToken();
            const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:8888' : ''}/.netlify/functions/getUserProfile`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.language) {
                  setCurrentLanguage(data.language);
                  i18n.changeLanguage(data.language);
                }
                setIsAdmin(data.role === 'admin');
                setUserPlan(data.plan || "free");
            }
        } catch (e) {
            console.error("Error fetching user profile", e);
        }
      } else {
        setIsAdmin(false);
      }

      setLoading(false);

      // Redirection intelligente après login
      if (user && location.pathname === "/login") {
        navigate("/dashboard");
      }
    });

    return () => { 
        mounted = false; 
        unsubscribe(); 
    };
  }, [navigate, location]);

  const loginWithGoogle = async () => {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;

      await createUserInDatabase(user, user.displayName || "", "");

      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to log in with Google", error);
      // Pas de throw error ici pour ne pas crasher l'UI, on log juste l'erreur
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to log in with email and password", error);
      throw error; // Re-throw error so it can be handled by the caller (Login.tsx)
    }
  };

  const signupWithEmail = async (email: string, password: string, companyName: string, howDidYouHear: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      await createUserInDatabase(user, companyName, howDidYouHear);

      return userCredential;
    } catch (error) {
      console.error("Failed to sign up", error);
      throw error;
    }
  };

  const loginWithCustomTokenFunc = async (token: string) => {
      try {
          await signInWithCustomToken(auth, token);
          navigate("/dashboard");
      } catch (error) {
          console.error("Failed to login with custom token", error);
          throw error;
      }
  };

  const createUserInDatabase = async (user: User, companyName: string, howDidYouHear: string) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:8888' : ''}/.netlify/functions/createUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email,
          companyName,
          howDidYouHear,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create user in the database");
      }
    } catch (error) {
      console.error("Error creating user in the database", error);
      // On ne bloque pas le flux si la création en BDD échoue (l'auth firebase est ok)
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Failed to logout", error);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Failed to send password reset email", error);
      throw error;
    }
  };

  const checkLoginMethods = async (email: string): Promise<string[]> => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods;
    } catch (error) {
      console.error("Failed to fetch sign in methods", error);
      return [];
    }
  };

  const getToken = async () => {
    if (currentUser) {
      return await currentUser.getIdToken();
    }
    return null;
  };

  const changeLanguage = async (language: string) => {
    // Optimistic UI update: change language immediately
    setCurrentLanguage(language);
    await i18n.changeLanguage(language);

    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:8888' : ''}/.netlify/functions/updateUserLanguage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ language }),
        });
      } catch (error) {
        console.error("Failed to update language on server:", error);
        // We don't revert here to avoid jarring UX, user will just have old language on next login if it fails
      }
    }
  };

  const value = {
    currentUser,
    loading,
    isAdmin,
    userPlan,
    loginWithGoogle,
    loginWithEmail,
    signupWithEmail,
    loginWithCustomToken: loginWithCustomTokenFunc,
    logout,
    resetPassword,
    checkLoginMethods,
    getToken,
    changeLanguage,
    currentLanguage,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingOverlay /> : children}
    </AuthContext.Provider>
  );
};
